import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, HelpCircle, Keyboard } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useGame, useSubmitMultiScore, useSubmitSoloScore, } from '@/features/games/_framework/api';
import { useCheckMyAchievements } from '@/features/achievements/api';
import { useCheckMyCosmetics } from '@/features/cosmetics/api';
import { useCheckMyMissions } from '@/features/battle-pass/api';
import { getMiniGame } from '@/features/games/_framework/registry';
import { useRoom, useRoomMembers } from '@/features/rooms/api';
import { useRoomChannel } from '@/features/rooms/useRoomChannel';
// Note : on monte useRoomChannel au niveau GameSession pour les games multi
// afin que les events Realtime sur games (status='finished' lors de la
// finalisation par le host) arrivent à TOUS les clients pendant qu'ils
// jouent. Sans ça, seul le host (qui déclenche finalize via mutation
// invalidation) verrait le podium se révéler.
import { Card, CardDescription, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton, Spinner } from '@/components/ui/Loading';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { LiveMultiOverlay } from '@/features/games/_framework/LiveMultiOverlay';
import { SoloResultScreen } from '@/features/games/_framework/SoloResultScreen';
import { MultiPodiumScreen } from '@/features/games/_framework/MultiPodiumScreen';
export function GamePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { data: game, isLoading } = useGame(id);
    if (!user) {
        return (_jsx(Card, { padding: "lg", className: "mx-auto max-w-md text-center", children: _jsx(CardTitle, { children: "Connecte-toi pour jouer" }) }));
    }
    if (isLoading)
        return _jsx(Skeleton, { className: "h-96 w-full" });
    if (!game || !id)
        return _jsx(NotFoundCard, { onBack: () => navigate('/') });
    return _jsx(GameSession, { game: game, currentPlayerId: user.id, onExit: () => navigate('/') });
}
// ────────────────────────────────────────────────────────────────────────────
// GameSession — orchestrateur du cycle de vie
// ────────────────────────────────────────────────────────────────────────────
function GameSession({ game, currentPlayerId, onExit, }) {
    const def = getMiniGame(game.type);
    const isMulti = game.mode === 'multi';
    // Multi : on garde le canal Realtime de la room ouvert pendant toute la
    // game pour recevoir l'event UPDATE games quand le host finalise. Sans
    // ça, les autres joueurs ne sauraient jamais que la manche est terminée.
    const { data: roomForChannel } = useRoom(isMulti ? game.room_id ?? undefined : undefined);
    useRoomChannel({
        roomId: isMulti ? game.room_id ?? undefined : undefined,
        code: isMulti ? roomForChannel?.code : undefined,
    });
    // Multi : on charge la liste des joueurs (pseudo + avatar) pour la passer
    // aux mini-jeux (sinon ils retombent sur des UUID tronqués via shortenId).
    const { data: roomMembersForGame } = useRoomMembers(isMulti ? game.room_id ?? undefined : undefined);
    const playersForGame = useMemo(() => {
        if (!isMulti || !roomMembersForGame)
            return undefined;
        return roomMembersForGame.map((m) => ({
            player_id: m.player_id,
            pseudo: m.pseudo,
            avatar_url: m.avatar_url,
            level: m.level,
        }));
    }, [isMulti, roomMembersForGame]);
    // Phase locale (intro → playing → submitting → finished)
    const initialPhase = !def
        ? 'unsupported'
        : isMulti
            ? 'intro' // multi : on attend que l'user clique "Je suis prêt"
            : 'playing'; // solo : on plonge directement
    const [phase, setPhase] = useState(initialPhase);
    const [scoreSnapshot, setScoreSnapshot] = useState(null);
    if (!def) {
        return (_jsxs(Card, { padding: "lg", className: "mx-auto max-w-md text-center", children: [_jsx(CardTitle, { children: "Mini-jeu inconnu" }), _jsxs(CardDescription, { className: "mt-2", children: [_jsx("code", { className: "rounded bg-ink-800 px-1", children: game.type }), " n'est pas (encore) enregistr\u00E9 dans le framework."] }), _jsx("div", { className: "mt-4 flex justify-center", children: _jsx(Button, { variant: "primary", onClick: onExit, children: "Retour" }) })] }));
    }
    const showLiveOverlay = isMulti && game.room_id && (phase === 'playing' || phase === 'submitting') && !def.hasOwnLiveOverlay;
    return (
    // flex column qui prend toute la hauteur du <main> (parent contraint).
    // BackBar reste compacte, le contenu de phase prend l'espace restant.
    _jsxs("div", { className: "flex min-h-0 flex-1 flex-col gap-3 md:gap-4", children: [_jsx(BackBar, { onExit: onExit, game: game, def: def }), phase === 'intro' && (_jsx(IntroPanel, { game: game, def: def, onStart: () => setPhase('playing') })), phase === 'playing' && (_jsx(PlayingArea, { game: game, def: def, currentPlayerId: currentPlayerId, players: playersForGame, isHost: isMulti && roomForChannel?.host_id === currentPlayerId, onSubmit: async (s) => {
                    setScoreSnapshot(s);
                    setPhase('submitting');
                }, onAbort: async () => {
                    setPhase('aborted');
                } })), showLiveOverlay && game.room_id && (_jsx(LiveMultiOverlay, { gameId: game.id, roomId: game.room_id, currentPlayerId: currentPlayerId })), phase === 'submitting' && scoreSnapshot && (_jsx(Submitter, { game: game, submission: scoreSnapshot, 
                // En multi comme en solo, on bascule en 'finished' juste après
                // soumission. MultiPodiumScreen gère lui-même le sous-état
                // "manche en cours" (vue host avec bouton finalize) vs "podium
                // révélé" (game.status='finished').
                onDoneSolo: () => setPhase('finished'), onDoneMulti: () => setPhase('finished'), onError: () => {
                    // Sur erreur, on revient à playing pour permettre un retry
                    setPhase('playing');
                } })), phase === 'finished' && (isMulti ? (_jsx(MultiPodiumScreen, { game: game, currentPlayerId: currentPlayerId, onBackToLobby: () => {
                    if (game.room_id)
                        onExit(); // TODO: nav directe vers /lobby/CODE
                    else
                        onExit();
                } })) : (_jsx(SoloResultScreen, { game: game, submission: scoreSnapshot, onExit: onExit }))), phase === 'aborted' && (_jsxs(Card, { padding: "lg", className: "mx-auto max-w-md text-center", children: [_jsx(CardTitle, { children: "Partie abandonn\u00E9e" }), _jsx("div", { className: "mt-4 flex justify-center", children: _jsx(Button, { variant: "primary", onClick: onExit, children: "Retour" }) })] }))] }));
}
// ────────────────────────────────────────────────────────────────────────────
// Submitter : appelle la bonne RPC selon le mode et bascule la phase
// ────────────────────────────────────────────────────────────────────────────
function Submitter({ game, submission, onDoneSolo, onDoneMulti, onError, }) {
    const submitSolo = useSubmitSoloScore();
    const submitMulti = useSubmitMultiScore();
    const checkAch = useCheckMyAchievements();
    const checkCos = useCheckMyCosmetics();
    const checkMis = useCheckMyMissions();
    const { toast } = useToast();
    // Ref-guard contre le double-mount StrictMode dev : la RPC ne doit
    // partir qu'UNE fois. Sans ça, le 2e mount lève already_submitted.
    const firedRef = useRef(false);
    /** Évalue + toaste achievements & cosmétiques fraîchement débloqués.
     *  Best-effort : on laisse le flux continuer même en cas d'erreur. */
    async function notifyUnlocks() {
        try {
            const newAch = await checkAch.mutateAsync();
            for (const a of newAch) {
                toast({
                    tone: 'achievement',
                    title: 'Achievement débloqué !',
                    description: `${a.name} · +${a.xp_reward} XP (${a.rarity})`,
                });
            }
        }
        catch {
            /* ignore */
        }
        try {
            const newCos = await checkCos.mutateAsync();
            for (const c of newCos) {
                toast({
                    tone: 'achievement',
                    title: 'Nouveau cosmétique !',
                    description: `${c.name} (${c.type}) — équipe-le sur /cosmetics`,
                });
            }
        }
        catch {
            /* ignore */
        }
        try {
            const newMis = await checkMis.mutateAsync();
            for (const m of newMis) {
                toast({
                    tone: 'success',
                    title: 'Mission complétée',
                    description: `${m.name} · clique sur /battle-pass pour réclamer +${m.bpxp_reward} BPXP`,
                });
            }
        }
        catch {
            /* ignore */
        }
    }
    useEffect(() => {
        // Ref-guard : ne lance la soumission qu'une seule fois, même si
        // StrictMode dev double-mount le composant.
        if (firedRef.current)
            return;
        firedRef.current = true;
        // Le mini-jeu a déjà appelé sa propre RPC (ex: submit_typing_score).
        // Le score est calculé serveur, le framework saute la soumission.
        if (submission.preSubmitted) {
            void notifyUnlocks();
            if (game.mode === 'multi')
                onDoneMulti();
            else
                onDoneSolo();
            return;
        }
        void (async () => {
            try {
                if (game.mode === 'multi') {
                    await submitMulti.mutateAsync({
                        gameId: game.id,
                        score: submission.score,
                        meta: submission.meta,
                    });
                    // En multi, les achievements sont check à la finalisation côté
                    // host — chaque joueur fera son propre check au podium.
                    onDoneMulti();
                }
                else {
                    await submitSolo.mutateAsync({
                        gameId: game.id,
                        score: submission.score,
                        meta: submission.meta,
                        durationMs: submission.durationMs,
                    });
                    await notifyUnlocks();
                    onDoneSolo();
                }
            }
            catch (err) {
                toast({
                    tone: 'error',
                    title: 'Soumission rejetée',
                    description: err.message,
                });
                onError();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsxs(Card, { padding: "lg", className: "mx-auto max-w-md text-center", children: [_jsx(Spinner, {}), _jsx("p", { className: "mt-3 font-display text-lg uppercase tracking-tight text-ink-50", children: "Validation du score\u2026" }), _jsx(CardDescription, { className: "mt-1", children: "Le serveur recalcule la dur\u00E9e et applique l'anti-triche." })] }));
}
// ────────────────────────────────────────────────────────────────────────────
// IntroPanel (multi)
// ────────────────────────────────────────────────────────────────────────────
function IntroPanel({ game, def, onStart, }) {
    const { data: room } = useRoom(game.room_id ?? undefined);
    const { data: members = [] } = useRoomMembers(game.room_id ?? undefined);
    // useRoomChannel est désormais monté au niveau GameSession (parent),
    // donc le presence + invalidations Realtime sont déjà actifs ici. Pas
    // de re-mount du canal ici, sinon supabase.channel(name) réutilise
    // l'instance existante et le 2e .on() lève "after subscribe()".
    void room; // garde la référence pour TanStack Query (préchargement)
    return (_jsxs(Card, { variant: "glow", padding: "lg", className: "mx-auto max-w-2xl", children: [_jsxs(Badge, { variant: "flame", size: "lg", children: [def.emoji, " ", def.name] }), _jsx(CardTitle, { className: "mt-3 text-3xl", children: def.name }), _jsx(CardDescription, { className: "mt-2", children: def.description }), _jsxs("div", { className: "mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-3", children: [_jsxs("div", { className: "rounded-xl bg-ink-900/60 p-3", children: [_jsx("p", { className: "text-xs uppercase tracking-wider text-ink-400", children: "Cat\u00E9gorie" }), _jsx("p", { className: "mt-1 font-semibold text-ink-100 capitalize", children: def.category })] }), _jsxs("div", { className: "rounded-xl bg-ink-900/60 p-3", children: [_jsx("p", { className: "text-xs uppercase tracking-wider text-ink-400", children: "Joueurs" }), _jsx("p", { className: "mt-1 font-semibold text-ink-100", children: members.length })] }), _jsxs("div", { className: "rounded-xl bg-ink-900/60 p-3", children: [_jsx("p", { className: "text-xs uppercase tracking-wider text-ink-400", children: "Mode" }), _jsx("p", { className: "mt-1 font-semibold text-ink-100", children: "Multi" })] })] }), _jsx("div", { className: "mt-6 flex justify-center", children: _jsx(Button, { variant: "primary", size: "lg", onClick: onStart, children: "Je suis pr\u00EAt \u2014 c'est parti" }) })] }));
}
// ────────────────────────────────────────────────────────────────────────────
// PlayingArea : monte le composant lazy du mini-jeu
// ────────────────────────────────────────────────────────────────────────────
function PlayingArea({ game, def, currentPlayerId, players, isHost, onSubmit, onAbort, }) {
    const Component = useMemo(() => {
        return lazy(async () => {
            const mod = await def.load();
            return { default: mod.Component };
        });
    }, [def]);
    // Config = config DB ou rankedConfig en fallback
    const config = useMemo(() => ({ ...def.rankedConfig, ...game.config }), [def.rankedConfig, game.config]);
    const onSubmitMemo = useCallback((s) => onSubmit(s), [onSubmit]);
    const onAbortMemo = useCallback(() => onAbort(), [onAbort]);
    return (_jsx(Suspense, { fallback: _jsx(Skeleton, { className: "h-96 w-full" }), children: _jsx(Component, { gameId: game.id, currentPlayerId: currentPlayerId, mode: game.mode, config: config, seed: game.seed, players: players, isHost: isHost, onSubmit: onSubmitMemo, onAbort: onAbortMemo }) }));
}
// ────────────────────────────────────────────────────────────────────────────
// BackBar
// ────────────────────────────────────────────────────────────────────────────
function BackBar({ onExit, game, def, }) {
    const [helpOpen, setHelpOpen] = useState(false);
    return (_jsxs("div", { className: "flex items-center justify-between", children: [_jsx(Button, { variant: "ghost", size: "sm", leftIcon: _jsx(ArrowLeft, { className: "h-4 w-4" }), onClick: onExit, children: "Quitter" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs(Badge, { size: "sm", variant: "electric", children: [def.emoji, " ", def.name] }), _jsx(Badge, { size: "sm", children: game.mode }), _jsx("button", { type: "button", "aria-label": "R\u00E8gles du jeu", onClick: () => setHelpOpen(true), className: "grid h-8 w-8 place-items-center rounded-full bg-ink-800 text-ink-300 transition-colors hover:bg-ink-700 hover:text-ink-50 focus-visible:outline-2 focus-visible:outline-electric-400", children: _jsx(HelpCircle, { className: "h-4 w-4" }) })] }), _jsx(RulesModal, { open: helpOpen, onOpenChange: setHelpOpen, def: def })] }));
}
function RulesModal({ open, onOpenChange, def, }) {
    return (_jsx(Modal, { open: open, onOpenChange: onOpenChange, size: "lg", title: _jsxs("span", { className: "inline-flex items-center gap-2", children: [_jsx("span", { className: "text-2xl", children: def.emoji }), def.name] }), description: def.description, footer: _jsx(Button, { variant: "primary", onClick: () => onOpenChange(false), children: "Compris" }), children: _jsxs("div", { className: "space-y-5", children: [def.rules && def.rules.length > 0 ? (_jsxs("section", { children: [_jsx("h3", { className: "mb-2 font-display text-sm uppercase tracking-wider text-electric-300", children: "R\u00E8gles" }), _jsx("ul", { className: "space-y-2 text-sm text-ink-200", children: def.rules.map((r, i) => (_jsxs("li", { className: "flex gap-2", children: [_jsx("span", { className: "mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-electric-400" }), _jsx("span", { children: r })] }, i))) })] })) : (_jsx("p", { className: "text-sm text-ink-300", children: "Pas de r\u00E8gles d\u00E9taill\u00E9es pour ce mini-jeu \u2014 l'interface t'explique tout au fil de la partie." })), def.shortcuts && def.shortcuts.length > 0 && (_jsxs("section", { children: [_jsxs("h3", { className: "mb-2 inline-flex items-center gap-1 font-display text-sm uppercase tracking-wider text-flame-300", children: [_jsx(Keyboard, { className: "h-4 w-4" }), "Raccourcis"] }), _jsx("ul", { className: "space-y-1.5 text-sm text-ink-200", children: def.shortcuts.map((s, i) => (_jsxs("li", { className: "flex items-baseline gap-3", children: [_jsx("kbd", { className: "rounded-md border border-ink-700 bg-ink-900 px-2 py-0.5 font-mono text-xs text-ink-100 shadow-sm", children: s.keys }), _jsx("span", { className: "text-ink-300", children: s.action })] }, i))) })] }))] }) }));
}
function NotFoundCard({ onBack }) {
    return (_jsxs(Card, { padding: "lg", className: "mx-auto max-w-md text-center", children: [_jsx(CardTitle, { children: "Partie introuvable" }), _jsx(CardDescription, { className: "mt-2", children: "Cette partie n'existe pas, ou tu n'y es pas autoris\u00E9." }), _jsx("div", { className: "mt-4 flex justify-center", children: _jsx(Button, { variant: "primary", onClick: onBack, children: "Retour" }) })] }));
}
